import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faChalkboardTeacher, faEye, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { Card, Button, Table, Modal, Form, Badge, Row, Col, Alert, Pagination } from '@themesberg/react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import TeacherService from '../../services/teacher.service';
import ClassRoomService from '../../services/classroom.service';
import SubjectService from '../../services/subject.service';
import TeacherSubjectService from '../../services/teacherSubject.service';
import { useAcademicYear } from '../../contexts/AcademicYearContext';

const Teachers = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { selectedYear } = useAcademicYear();
  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    totalPages: 1
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherAssignments, setTeacherAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [schoolId, setSchoolId] = useState(null);

  // Dados do professor (Passo 1)
  const [teacherData, setTeacherData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Atribuições de turmas e disciplinas (Passo 2)
  const [assignments, setAssignments] = useState([
    { classroom: '', subject: '' }
  ]);

  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchInitialData = useCallback(async (school) => {
    try {
      setLoading(true);

      // Buscar turmas da escola
      const classroomsResponse = await ClassRoomService.getAll({ school });
      setClassrooms(classroomsResponse.results || classroomsResponse);

      // Buscar disciplinas da escola
      const subjectsResponse = await SubjectService.getAll({ school });
      setSubjects(subjectsResponse.results || subjectsResponse);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setLoading(false);
    }
  }, []);

  const fetchTeachers = useCallback(async (school, page = 1, params = {}, isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setFilterLoading(true);
      }

      // Buscar professores da escola com paginação e filtros
      const requestParams = { school, page, ...params };

      const teachersResponse = await TeacherService.getAll(requestParams);
      setTeachers(Array.isArray(teachersResponse) ? teachersResponse : (teachersResponse.results || []));

      // Atualizar paginação
      if (teachersResponse.count !== undefined) {
        setPagination({
          count: teachersResponse.count || 0,
          next: teachersResponse.next,
          previous: teachersResponse.previous,
          currentPage: page,
          totalPages: Math.ceil((teachersResponse.count || 0) / 10)
        });
      }

      if (isInitialLoad) {
        setLoading(false);
      } else {
        setFilterLoading(false);
      }
    } catch (err) {
      console.error('Error fetching teachers:', err);
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setFilterLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (user?.school_users) {
      const schoolUser = user.school_users.find(su =>
        (su.role === 'COORD' || su.role === 'PRINCIPAL') && su.active
      );
      if (schoolUser) {
        const schoolId = schoolUser.school?.id || schoolUser.school;
        setSchoolId(schoolId);
        fetchInitialData(schoolId);
        fetchTeachers(schoolId, 1, {}, true);
      }
    }
  }, [fetchInitialData, fetchTeachers, user]);

  useEffect(() => {
    if (schoolId) {
      const timeoutId = setTimeout(() => {
        const params = {};
        if (searchTerm) params.search = searchTerm;
        fetchTeachers(schoolId, 1, params, false);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSearch = () => {
    if (schoolId) {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      fetchTeachers(schoolId, 1, params, false);
    }
  };

  const handlePageChange = (page) => {
    if (schoolId) {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      fetchTeachers(schoolId, page, params);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
  };

  const handleOpenCreateModal = () => {
    setTeacherData({ name: '', email: '', phone: '' });
    setAssignments([{ classroom: '', subject: '' }]);
    setCurrentStep(1);
    setError(null);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCurrentStep(1);
    setError(null);
  };

  const handleView = (teacher) => {
    setSelectedTeacher(teacher);
    fetchTeacherAssignments(teacher.id);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedTeacher(null);
    setTeacherAssignments([]);
  };

  const fetchTeacherAssignments = async (teacherId) => {
    try {
      setAssignmentsLoading(true);
      const response = await TeacherSubjectService.getAll({
        teacher: teacherId,
        school: schoolId,
        year: selectedYear
      });
      setTeacherAssignments(response.results || response);
    } catch (err) {
      console.error('Error fetching teacher assignments:', err);
      showToast('Erro ao carregar atribuições', 'error');
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const handleEdit = (teacher) => {
    setSelectedTeacher(teacher);
    setTeacherData({
      name: teacher.name,
      email: teacher.email,
      phone: teacher.phone || ''
    });
    setError(null);
    setShowEditModal(true);
  };

  const handleManageAssignments = (teacher) => {
    setSelectedTeacher(teacher);
    fetchTeacherAssignments(teacher.id);
    setShowAssignmentsModal(true);
  };

  const handleCloseAssignmentsModal = () => {
    setShowAssignmentsModal(false);
    setSelectedTeacher(null);
    setTeacherAssignments([]);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedTeacher(null);
    setError(null);
  };

  const handleUpdateTeacher = async () => {
    try {
      // Atualizar apenas dados básicos do professor
      await TeacherService.update(selectedTeacher.id, {
        name: teacherData.name,
        phone: teacherData.phone
      });

      showToast('Professor atualizado com sucesso!', 'success');
      await fetchTeachers(schoolId, pagination.currentPage);
      handleCloseEditModal();
    } catch (err) {
      console.error('Error updating teacher:', err);
      const errorMsg = err.response?.data?.message || 'Erro ao atualizar professor. Tente novamente.';
      setError(errorMsg);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (window.confirm('Deseja realmente remover esta atribuição?')) {
      try {
        await TeacherSubjectService.delete(assignmentId);
        showToast('Atribuição removida com sucesso!', 'success');
        // Recarregar atribuições
        await fetchTeacherAssignments(selectedTeacher.id);
        // Atualizar lista de professores
        await fetchTeachers(schoolId, pagination.currentPage);
      } catch (err) {
        console.error('Error deleting assignment:', err);
        showToast('Erro ao remover atribuição', 'error');
      }
    }
  };

  const handleAddNewAssignment = async () => {
    // Adicionar nova linha vazia
    setTeacherAssignments([...teacherAssignments, { classroom: '', subject: '', isNew: true }]);
  };

  const handleAssignmentFieldChange = (index, field, value) => {
    const newAssignments = [...teacherAssignments];
    newAssignments[index][field] = value;
    setTeacherAssignments(newAssignments);
  };

  const handleSaveNewAssignment = async (index) => {
    const assignment = teacherAssignments[index];

    if (!assignment.classroom || !assignment.subject) {
      showToast('Selecione turma e disciplina', 'error');
      return;
    }

    try {
      await TeacherSubjectService.create({
        teacher: selectedTeacher.id,
        school: schoolId,
        classroom: assignment.classroom,
        subject: assignment.subject,
        year: selectedYear
      });

      showToast('Atribuição adicionada com sucesso!', 'success');
      // Recarregar atribuições
      await fetchTeacherAssignments(selectedTeacher.id);
      // Atualizar lista de professores
      await fetchTeachers(schoolId, pagination.currentPage);
    } catch (err) {
      console.error('Error creating assignment:', err);
      const errorMsg = err.response?.data?.message || err.response?.data?.non_field_errors?.[0] || 'Erro ao criar atribuição';
      showToast(errorMsg, 'error');
    }
  };

  const handleCancelNewAssignment = (index) => {
    const newAssignments = teacherAssignments.filter((_, i) => i !== index);
    setTeacherAssignments(newAssignments);
  };

  const handleTeacherDataChange = (e) => {
    const { name, value } = e.target;
    setTeacherData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignmentChange = (index, field, value) => {
    const newAssignments = [...assignments];
    newAssignments[index][field] = value;
    setAssignments(newAssignments);
  };

  const handleAddAssignment = () => {
    setAssignments([...assignments, { classroom: '', subject: '' }]);
  };

  const handleRemoveAssignment = (index) => {
    if (assignments.length > 1) {
      const newAssignments = assignments.filter((_, i) => i !== index);
      setAssignments(newAssignments);
    }
  };

  const handleNextStep = () => {
    // Validar dados do passo 1
    if (!teacherData.name || !teacherData.email) {
      setError('Nome e email são obrigatórios');
      return;
    }

    setError(null);
    setCurrentStep(2);
  };

  const handlePreviousStep = () => {
    setError(null);
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    try {
      // Validar atribuições
      const validAssignments = assignments.filter(a => a.classroom && a.subject);

      if (validAssignments.length === 0) {
        setError('É necessário atribuir pelo menos uma turma e disciplina');
        return;
      }

      const payload = {
        ...teacherData,
        school: schoolId,
        assignments: validAssignments
      };

      const response = await TeacherService.createTeacher(payload);

      showToast(response.message || 'Professor criado com sucesso! Um email foi enviado para definição de senha.', 'success');

      await fetchTeachers(schoolId, pagination.currentPage);
      handleCloseCreateModal();
    } catch (err) {
      console.error('Error creating teacher:', err);
      const errorMsg = err.response?.data?.email?.[0] ||
                       err.response?.data?.assignments?.[0] ||
                       err.response?.data?.message ||
                       'Erro ao criar professor. Tente novamente.';
      setError(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Gestão de Professores</h4>
          <p className="mb-0">Gerencie os professores da sua escola</p>
        </div>
      </div>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={9}>
              <Form.Group>
                <Form.Label>Buscar Professor</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o nome ou email do professor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="secondary" onClick={handleClearFilters} className="w-100">
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faChalkboardTeacher} className="me-2" />
                    Lista de Professores
                  </h5>
                </Col>
                <Col className="text-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleOpenCreateModal}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Novo Professor
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Table responsive className="align-items-center table-flush">
            <thead className="thead-light">
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Telefone</th>
                <th>Turmas/Disciplinas</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filterLoading ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Buscando...</span>
                    </div>
                    <span className="ms-2 text-muted">Buscando professores...</span>
                  </td>
                </tr>
              ) : teachers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <FontAwesomeIcon icon={faChalkboardTeacher} size="3x" className="text-muted mb-3" />
                    <p className="text-muted">Nenhum professor encontrado</p>
                  </td>
                </tr>
              ) : (
                teachers.map((teacher) => (
                  <tr key={teacher.id}>
                    <td className="fw-bold">{teacher.name}</td>
                    <td>{teacher.email}</td>
                    <td>{teacher.phone}</td>
                    <td>
                      <Badge bg="info" className="me-1">
                        {teacher.assignments_count || 0} atribuições
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="info"
                        size="sm"
                        className="me-2"
                        onClick={() => handleView(teacher)}
                        title="Visualizar detalhes"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(teacher)}
                        title="Editar professor"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleManageAssignments(teacher)}
                        title="Gerenciar atribuições"
                      >
                        <FontAwesomeIcon icon={faClipboardList} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
        {teachers.length > 0 && (
          <Card.Footer className="px-3 border-0 d-flex justify-content-center">
            <Pagination className="mb-0">
              <Pagination.Prev
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.previous}
              />
              {[...Array(pagination.totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === pagination.currentPage}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.next}
              />
            </Pagination>
          </Card.Footer>
        )}
      </Card>
        </Col>
      </Row>

      {/* Modal de Criação em 2 Passos */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Novo Professor - {currentStep} de 2
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {/* Passo 1: Dados do Professor */}
          {currentStep === 1 && (
            <>
              <h5 className="mb-3">Dados do Professor</h5>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Nome Completo *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={teacherData.name}
                    onChange={handleTeacherDataChange}
                    placeholder="Digite o nome completo"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={teacherData.email}
                    onChange={handleTeacherDataChange}
                    placeholder="email@exemplo.com"
                    required
                  />
                  <Form.Text className="text-muted">
                    Um email será enviado para este endereço com o link para definir a senha
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Telefone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={teacherData.phone}
                    onChange={handleTeacherDataChange}
                    placeholder="(00) 00000-0000"
                  />
                </Form.Group>
              </Form>
            </>
          )}

          {/* Passo 2: Turmas e Disciplinas */}
          {currentStep === 2 && (
            <>
              <h5 className="mb-3">Atribuições de Turmas e Disciplinas</h5>
              <p className="text-muted mb-2">
                Adicione as turmas e disciplinas que este professor irá lecionar
              </p>

              {assignments.map((assignment, index) => (
                <div key={index} className="border rounded p-2 mb-2 bg-light">
                  <Row className="align-items-end">
                    <Col md={5}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small mb-1">Turma *</Form.Label>
                        <Form.Select
                          size="sm"
                          value={assignment.classroom}
                          onChange={(e) => handleAssignmentChange(index, 'classroom', e.target.value)}
                          required
                        >
                          <option value="">Selecione uma turma</option>
                          {classrooms.map((classroom) => (
                            <option key={classroom.id} value={classroom.id}>
                              {classroom.grade_name} - {classroom.grade_education_level} {classroom.name} - {classroom.shift_display}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={5}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small mb-1">Disciplina *</Form.Label>
                        <Form.Select
                          size="sm"
                          value={assignment.subject}
                          onChange={(e) => handleAssignmentChange(index, 'subject', e.target.value)}
                          required
                        >
                          <option value="">Selecione uma disciplina</option>
                          {subjects.map((subject) => (
                            <option key={subject.id} value={subject.id}>
                              {subject.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={2} className="text-end">
                      {assignments.length > 1 && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveAssignment(index)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </Col>
                  </Row>
                </div>
              ))}

              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddAssignment}
                className="mb-3"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Adicionar Outra Atribuição
              </Button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {currentStep === 1 ? (
            <>
              <Button variant="secondary" onClick={handleCloseCreateModal}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleNextStep}>
                Próximo
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={handlePreviousStep}>
                Voltar
              </Button>
              <Button variant="primary" onClick={handleSubmit}>
                Criar Professor
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal de Edição (Dados Básicos) */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Professor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome Completo *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={teacherData.name}
                onChange={handleTeacherDataChange}
                placeholder="Digite o nome completo"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={teacherData.email}
                disabled
                className="bg-light"
              />
              <Form.Text className="text-muted">
                O email não pode ser alterado
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Telefone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={teacherData.phone}
                onChange={handleTeacherDataChange}
                placeholder="(00) 00000-0000"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleUpdateTeacher}>
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Gerenciamento de Atribuições */}
      <Modal show={showAssignmentsModal} onHide={handleCloseAssignmentsModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Gerenciar Atribuições - {selectedTeacher?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Adicione, visualize ou remova as atribuições de turmas e disciplinas para o ano {selectedYear}
          </p>

          {assignmentsLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando atribuições...</span>
              </div>
            </div>
          ) : (
            <>
              {teacherAssignments.length === 0 && !teacherAssignments.some(a => a.isNew) ? (
                <Alert variant="info">
                  Este professor ainda não possui atribuições para o ano {selectedYear}
                </Alert>
              ) : (
                <div className="mb-3">
                  {teacherAssignments.map((assignment, index) => (
                    <div key={assignment.id || index} className="border rounded p-3 mb-2">
                      {assignment.isNew ? (
                        // Nova atribuição (formulário)
                        <Row className="align-items-end">
                          <Col md={5}>
                            <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">Turma *</Form.Label>
                              <Form.Select
                                size="sm"
                                value={assignment.classroom}
                                onChange={(e) => handleAssignmentFieldChange(index, 'classroom', e.target.value)}
                                required
                              >
                                <option value="">Selecione uma turma</option>
                                {classrooms.map((classroom) => (
                                  <option key={classroom.id} value={classroom.id}>
                                    {classroom.grade_name} - {classroom.grade_education_level} {classroom.name} - {classroom.shift_display}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>

                          <Col md={4}>
                            <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">Disciplina *</Form.Label>
                              <Form.Select
                                size="sm"
                                value={assignment.subject}
                                onChange={(e) => handleAssignmentFieldChange(index, 'subject', e.target.value)}
                                required
                              >
                                <option value="">Selecione uma disciplina</option>
                                {subjects.map((subject) => (
                                  <option key={subject.id} value={subject.id}>
                                    {subject.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>

                          <Col md={3} className="text-end">
                            <Button
                              variant="success"
                              size="sm"
                              className="me-1"
                              onClick={() => handleSaveNewAssignment(index)}
                            >
                              Salvar
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleCancelNewAssignment(index)}
                            >
                              Cancelar
                            </Button>
                          </Col>
                        </Row>
                      ) : (
                        // Atribuição existente (visualização)
                        <Row className="align-items-center">
                          <Col md={9}>
                            <div>
                              <strong>Turma:</strong> {assignment.classroom_grade} {assignment.classroom_name} - {assignment.classroom_shift}
                            </div>
                            <div>
                              <strong>Disciplina:</strong> {assignment.subject_name}
                            </div>
                            <div className="text-muted small">
                              <strong>Alunos:</strong> {assignment.students_count || 0}
                            </div>
                          </Col>
                          <Col md={3} className="text-end">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteAssignment(assignment.id)}
                              title="Remover atribuição"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddNewAssignment}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Adicionar Nova Atribuição
              </Button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAssignmentsModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Visualização Expandido */}
      <Modal show={showViewModal} onHide={handleCloseViewModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes do Professor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="mb-3">Informações Básicas</h5>
          <Row className="mb-2">
            <Col md={12}><strong>Nome:</strong> {selectedTeacher?.name || '-'}</Col>
          </Row>
          <Row className="mb-2">
            <Col md={12}><strong>Email:</strong> {selectedTeacher?.email || '-'}</Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}><strong>Telefone:</strong> {selectedTeacher?.phone || '-'}</Col>
          </Row>

          <hr />

          <h5 className="mb-3">
            Atribuições de Turmas e Disciplinas ({selectedYear})
            <Badge bg="info" className="ms-2">{teacherAssignments.length}</Badge>
          </h5>

          {assignmentsLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando atribuições...</span>
              </div>
            </div>
          ) : teacherAssignments.length === 0 ? (
            <Alert variant="info">
              Este professor não possui atribuições para o ano {selectedYear}
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Turma</th>
                    <th>Turno</th>
                    <th>Disciplina</th>
                    <th>Alunos</th>
                  </tr>
                </thead>
                <tbody>
                  {teacherAssignments.map((assignment) => (
                    <tr key={assignment.id}>
                      <td>
                        {assignment.classroom_grade} - {assignment.classroom_education_level} {assignment.classroom_name}
                      </td>
                      <td>{assignment.classroom_shift}</td>
                      <td>{assignment.subject_name}</td>
                      <td>
                        <Badge bg="secondary">{assignment.students_count || 0}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseViewModal}>
            Fechar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleCloseViewModal();
              handleEdit(selectedTeacher);
            }}
          >
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Editar Professor
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Teachers;
